import { useEffect, useMemo, useRef, useState } from "react";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { dashboardInsightsService } from "../../services/dashboardInsightsService";
import { DashboardDistributionResponseDto, DashboardRtuRowDto } from "../../types/DashboardInsights";
import { RTU } from "../../types/RTU";
import { useNavigate } from "react-router-dom";
import { normalizeImageVersion } from "./dashboardVersionNormalization";
import { formatDisplayDateTime } from "../../utils/dateTimeFormat";
import { formatProducerName } from "../../utils/rtuDefaults";
import { useTranslation } from "react-i18next";
import { SkeletonRegion, SkeletonRow } from "../common/Skeleton/Skeleton";

const ROW_COLORS = [
  "#B13F5B",
  "#2077B4",
  "#D5A827",
  "#2D9C92",
  "#7B5ED7",
  "#7B8791",
];

interface ImageVersionDistributionProps {
  rtus?: RTU[];
}

interface ImageVersionRow {
  key: string;
  label: string;
  count: number;
  percentage: number;
  color: string;
}

type ImageVersionDeviceRow = DashboardRtuRowDto | RTU;

const isRtuDeviceRow = (row: ImageVersionDeviceRow): row is RTU => "publicIPAddress" in row;

const toPercent = (count: number, total: number) =>
  total > 0 ? Math.round((count / total) * 10000) / 100 : 0;

const formatPercent = (value: number) => {
  const rounded = Math.round(value * 100) / 100;
  return rounded.toFixed(2);
};

const getRtuImageVersion = (rtu: RTU): string => normalizeImageVersion(rtu.imageVersion);

const buildFallbackDistribution = (rtus: RTU[]): DashboardDistributionResponseDto => {
  const counts = rtus.reduce((acc, rtu) => {
    const key = getRtuImageVersion(rtu);
    acc.set(key, (acc.get(key) ?? 0) + 1);
    return acc;
  }, new Map<string, number>());

  const total = rtus.length;
  const buckets = Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([key, count]) => ({
      key,
      label: key,
      count,
      percentage: toPercent(count, total),
    }));

  return {
    generatedAtUtc: new Date().toISOString(),
    totalCount: total,
    buckets,
    devices: rtus.map((rtu) => ({
      id: rtu.id,
      name: rtu.name,
      producer: rtu.provider,
      targetIp: rtu.publicIPAddress,
      imageVersion: getRtuImageVersion(rtu),
      lastFileReceivedAtUtc: rtu.lastData,
    })),
  };
};

const getDeviceName = (row: ImageVersionDeviceRow): string => row.name?.trim() || `RTU ${row.id}`;

const getDeviceProducer = (row: ImageVersionDeviceRow): string =>
  formatProducerName(isRtuDeviceRow(row) ? row.provider : row.producer);

const getDeviceTarget = (row: ImageVersionDeviceRow): string =>
  (isRtuDeviceRow(row) ? row.publicIPAddress : row.targetIp) || "-";

const getDeviceImageVersion = (row: ImageVersionDeviceRow): string =>
  normalizeImageVersion(row.imageVersion);

const getDeviceLastSeen = (row: ImageVersionDeviceRow): string | undefined | null =>
  isRtuDeviceRow(row) ? row.lastData : row.lastFileReceivedAtUtc;

export const ImageVersionDistribution = ({ rtus = [] }: ImageVersionDistributionProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [distribution, setDistribution] = useState<DashboardDistributionResponseDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedBucket, setSelectedBucket] = useState<ImageVersionRow | null>(null);
  // The live RTU list is pushed over WebSocket and its array reference
  // changes on every fleet status update (roughly every few seconds on a
  // busy fleet). It is only needed here as a fallback if the dedicated
  // endpoint fails, so read it via a ref instead of an effect dependency -
  // otherwise this card would refetch on every unrelated real-time update.
  const rtusRef = useRef(rtus);
  useEffect(() => { rtusRef.current = rtus; }, [rtus]);

  useEffect(() => {
    let active = true;

    setLoading(true);
    dashboardInsightsService
      .imageVersions()
      .then((response) => {
        if (active) {
          setDistribution(response);
        }
      })
      .catch(() => {
        if (active) {
          setDistribution(buildFallbackDistribution(rtusRef.current));
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  const rows = useMemo<ImageVersionRow[]>(() => {
    const buckets = distribution?.buckets ?? [];
    const total = distribution?.totalCount ?? 0;
    const countsByVersion = buckets.reduce((counts, bucket) => {
      const rawVersion = bucket.key?.trim() ? bucket.key : bucket.label;
      const version = normalizeImageVersion(rawVersion);
      counts.set(version, (counts.get(version) ?? 0) + bucket.count);
      return counts;
    }, new Map<string, number>());

    return Array.from(countsByVersion.entries())
      .sort(([, leftCount], [, rightCount]) => rightCount - leftCount)
      .slice(0, 8)
      .map(([version, count], index) => ({
        key: version,
        label: version,
        count,
        percentage: toPercent(count, total),
        color: ROW_COLORS[Math.min(index, ROW_COLORS.length - 1)]!,
      }));
  }, [distribution?.buckets, distribution?.totalCount]);

  const selectedDevices = useMemo(() => {
    if (!selectedBucket) {
      return [];
    }

    const devices = distribution?.devices ?? [];
    return devices.filter((device) => {
      const version = getDeviceImageVersion(device);
      return version === selectedBucket.key;
    });
  }, [distribution?.devices, selectedBucket]);

  const openDevice = (row: ImageVersionDeviceRow) => {
    navigate(`/rtupage/${row.id}`);
  };

  if (loading && !distribution) {
    return (
      <SkeletonRegion label={t('common.loading', { defaultValue: 'Loading' })} className="dashboard-distribution-kpi">
        <SkeletonRow count={4} withIcon={false} />
      </SkeletonRegion>
    );
  }

  if (!rows.length) {
    return (
      <div className="dashboard-distribution-kpi dashboard-distribution-kpi--empty" role="status">
        -
      </div>
    );
  }

  return (
    <>
      <div className="dashboard-distribution-kpi" role="list">
        {rows.map((row) => (
          <button
            key={row.key}
            type="button"
            className="dashboard-distribution-kpi__row"
            onClick={() => setSelectedBucket(row)}
            aria-label={row.label}
          >
            <div className="dashboard-distribution-kpi__row-top">
              <span className="dashboard-distribution-kpi__label" title={row.label}>
                {row.label}
              </span>
              <span className="dashboard-distribution-kpi__pct">{formatPercent(row.percentage)}%</span>
            </div>
            <div className="dashboard-distribution-kpi__bar-track">
              <span
                className="dashboard-distribution-kpi__bar"
                style={{
                  width: `${Math.min(100, row.percentage)}%`,
                  background: row.color,
                }}
              />
            </div>
            <span className="dashboard-distribution-kpi__count">
              {row.count} / {distribution?.totalCount ?? 0}
            </span>
          </button>
        ))}
      </div>

      <Dialog
        header={selectedBucket ? `Image version: ${selectedBucket.label}` : ""}
        visible={selectedBucket !== null}
        modal
        className="dashboard-status-detail-dialog"
        style={{ width: "min(64rem, calc(100vw - 7rem))", maxWidth: "96vw" }}
        contentStyle={{ padding: "0.75rem 1rem 1rem" }}
        headerStyle={{ padding: "0.75rem 1rem", borderBottom: "1px solid #e9ecef" }}
        onHide={() => setSelectedBucket(null)}
        blockScroll
      >
        <DataTable
          value={selectedDevices}
          emptyMessage="No RTUs for this image version."
          scrollable
          scrollHeight="360px"
          showGridlines
          size="small"
          stripedRows
          rowClassName={() => "dashboard-status-detail-dialog__row"}
          onRowClick={(event) => openDevice(event.data as ImageVersionDeviceRow)}
          tableStyle={{ width: "100%", tableLayout: "fixed" }}
        >
          <Column
            field="id"
            header={t("dashboard.tables.columns.wellId", { defaultValue: "Well ID" })}
            style={{ width: "7rem" }}
            bodyStyle={{ textAlign: "center" }}
          />
          <Column
            header={t("dashboard.tables.columns.rtuId", { defaultValue: "RTU ID" })}
            body={(row: ImageVersionDeviceRow) => getDeviceName(row)}
            style={{ width: "22%" }}
          />
          <Column header="Producer" body={(row: ImageVersionDeviceRow) => getDeviceProducer(row)} style={{ width: "20%" }} />
          <Column header="Public IP" body={(row: ImageVersionDeviceRow) => getDeviceTarget(row)} style={{ width: "18%" }} />
          <Column
            header="Last File"
            body={(row: ImageVersionDeviceRow) => formatDisplayDateTime(getDeviceLastSeen(row), "-")}
            style={{ width: "22%" }}
          />
        </DataTable>
      </Dialog>
    </>
  );
};

export default ImageVersionDistribution;
